import React, { Component, createRef } from "react";
import * as d3 from "d3";
import noDataPlaceholder from "../../assets/img/nodata_available.svg";

export interface ChartData {
  label: string;
  value: number;
}

interface Props {
  data: ChartData[];
  barColor?: string;
  valueColor?: string;
  labelColor?: string;
  width?: number;
}

class HorizontalPercentageBarChart extends Component<Props> {
  private svgRef = createRef<SVGSVGElement>();
  private tooltipDiv: HTMLDivElement | null = null;

  componentDidMount() {
    this.ensureTooltipDiv();
    this.renderChart();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.data !== this.props.data) {
      this.renderChart();
    }
  }

    /** Create a reusable floating tooltip div once */
  private ensureTooltipDiv() {
    if (this.tooltipDiv && document.body.contains(this.tooltipDiv)) return;

    const div = document.createElement("div");
    div.style.position = "fixed";
    div.style.pointerEvents = "none";
    div.style.zIndex = "9999";
    div.style.background = "rgba(0,0,0,0.85)";
    div.style.color = "#fff";
    div.style.padding = "6px 8px";
    div.style.borderRadius = "6px";
    div.style.fontFamily = "Poppins, sans-serif";
    div.style.fontSize = "12px";
    div.style.lineHeight = "1.3";
    div.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
    div.style.opacity = "0";
    div.style.transform = "translate(-50%, -120%)";
    div.style.transition = "opacity 120ms ease-out";
    document.body.appendChild(div);
    this.tooltipDiv = div;
  }

  /** Truncate label and add ellipsis if longer than max */
  private truncateLabel = (text: string, max = 21) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "…" : text;
  };

  /** Escape HTML for tooltip content safety */
  private escapeHtml(str: string) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /** Show tooltip with (label, value) at mouse position */
  private showTooltip = (label: string, value: number, diff: number | null, evt: MouseEvent) => {
    if (!this.tooltipDiv) return;
    const arrow =
      diff === null || diff === 0
        ? ""
        : diff > 0
        ? "▲"
        : "▼";

    this.tooltipDiv.innerHTML = `
      <div style="font-weight:600; margin-bottom:2px;">${this.escapeHtml(
      label
    )}</div>
      <div style="opacity:0.9;">${value}% ${diff !== null ? `(${arrow}${diff})` : ""}</div>
    `;
    this.tooltipDiv.style.opacity = "1";

    // Position near mouse; keep within viewport
    const padding = 10;
    const { clientX, clientY } = evt;
    const rect = this.tooltipDiv.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = clientX + 12;
    let y = clientY - 12;

    if (x + rect.width + padding > vw) x = vw - rect.width - padding;
    if (y + rect.height + padding > vh) y = vh - rect.height - padding;
    if (y < padding) y = padding;

    this.tooltipDiv.style.left = `${x}px`;
    this.tooltipDiv.style.top = `${y}px`;
  };

  /** Hide tooltip */
  private hideTooltip = () => {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.style.opacity = "0";
  };

  private wrapText = (
    textSelection: d3.Selection<SVGTextElement, string, SVGGElement, unknown>,
    maxWidth: number,
    maxLines: number = 2
  ) => {
    textSelection.each(function (d) {
      const textSel = d3.select(this);

      // Ensure we always have a string
      const rawText = typeof d === "string" ? d : textSel.text();
      if (!rawText || rawText.trim().length === 0) return;

      const words = rawText.split(/\s+/);
      if (!words || words.length === 0) return;

      let line: string[] = [];
      let lineNumber = 0;
      const lineHeight = 1.1;
      const x = textSel.attr("x") || 0;

      textSel.text(null);
      let tspan = textSel.append("tspan").attr("x", x).attr("dy", "0em");

      for (let i = 0; i < words.length; i++) {
        line.push(words[i]);
        tspan.text(line.join(" "));
        const tooWide = (tspan.node() as SVGTextElement).getComputedTextLength() > maxWidth;

        if (tooWide) {
          line.pop();
          tspan.text(line.join(" "));
          lineNumber++;

          if (lineNumber >= maxLines) {
            let finalText = line.join(" ") + "…";
            tspan.text(finalText);

            while (
              finalText.length > 0 &&
              (tspan.node() as SVGTextElement).getComputedTextLength() > maxWidth
              ) {
              finalText = finalText.slice(0, -2) + "…";
              tspan.text(finalText);
            }
            return;
          }

          line = [words[i]];
          tspan = textSel.append("tspan")
            .attr("x", x)
            .attr("dy", `${lineHeight}em`)
            .text(words[i]);
        }
      }
    });
  };

  renderChart = () => {
    const {
      data,
      barColor = "#2F4858",
      valueColor = "#333",
      labelColor = "#333",
      width = 700
    } = this.props;

    if (!data || !data.length) return;

    const isMobile = window.innerWidth <= 1024;


    const normalizedData = data.map((d: any) => {
      // comparison mode
      if (d.cubic_share_diff !== undefined) {
        return {
          label: d.label,
          value: d.value,
          diff: d.cubic_share_diff
        };
      }
      if (d.cubic_share_diff !== undefined) {
        return {
          label: d.label,
          value: d.value,
          diff: d.cubic_share_diff
        };
      }
      // normal mode
      return {label: d.label, value: d.value, diff: null};
    });
    const barHeight = 29;
    const barSpacing = 15;
    const calculatedHeight = normalizedData.length * (barHeight + barSpacing);

    const margin = { top: 20, right: 80, bottom: 20, left: 110 };
    const height = calculatedHeight + margin.top + margin.bottom;

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = calculatedHeight;

    const svg = d3.select(this.svgRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMinYMin meet")
      .style("width", "100%")
      .style("height", "auto");

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleLinear()
      .domain([0, 100])
      .range([0, innerWidth]);

    const y = d3.scaleBand()
      .domain(normalizedData.map(d => d.label))
      .range([0, innerHeight])
      .padding(0.3);

    // LEFT LABELS ONLY (No lines, no path)
    // chart.append("g")
    //   .call(d3.axisLeft(y).tickSize(0))
    //   .call(g => g.select(".domain").remove())
    //   .selectAll("text")
    //   .attr("dx", "-100")
    //   .style("text-anchor", "start")
    //   .style("fill", labelColor)
    //   .style("font-family", "Poppins, sans-serif")
    //   .style("font-weight", "400")
    //   .style("font-size", "12px");

    const yAxis = d3
      .axisLeft(y)
      .tickSize(0)
      // .tickFormat((d: string) => this.truncateLabel(d, 21));
      .tickFormat((d: string) => d);

    const yAxisG = chart.append("g").call(yAxis);

    const labelWidth = Math.abs(parseInt("-100")) - 10
    yAxisG.call(g => g.select(".domain").remove());

    yAxisG
      .selectAll<SVGTextElement, string>("text")
      // .attr("dx", "-100")
      .attr("x", -100)
      .attr("dy", "0.35em")
      .style("text-anchor", "start")
      .style("fill", labelColor)
      .style("font-family", "Poppins, sans-serif")
      .style("font-weight", "400")
      .style("font-size", "12px")
      .call((text) => this.wrapText(text, labelWidth, 2))
      .each(function(d: string) {
        const text = d3.select(this);
        text.select("title").remove();
        text.append("title").text(d); // native tooltip fallback
      })
      .on("mouseover", (event: MouseEvent, d: string) => {
        const datum = normalizedData.find(x => x.label === d);
        if (!datum) return;
        this.showTooltip(datum.label, datum.value, datum.diff ?? null, event);
      })
      .on("mousemove", (event: MouseEvent, d: string) => {
        const datum = normalizedData.find(x => x.label === d);
        if (!datum) return;
        this.showTooltip(datum.label, datum.value, datum.diff ?? null, event);
      })
      .on("mouseout", () => {
        this.hideTooltip();
      });

    // Bars
    chart.selectAll("rect")
      .data(normalizedData)
      .enter()
      .append("rect")
      .attr("x", 5)
      .attr("y", d => y(d.label)!)
      .attr("height", barHeight)
      .attr("width", 0)
      .attr("fill", barColor)
      .attr("rx", 6)
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attr("width", d => x(Math.min(d.value, 100)));

    // VALUES OUTSIDE (ALL SAME ALIGNMENT)
    chart.selectAll(".value-label")
      .data(normalizedData)
      .enter()
      .append("text")
      .attr("class", "value-label")
      .attr("x", innerWidth + (isMobile ? -7 : -15))
      .attr("y", d => y(d.label)! + barHeight / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "start")
      .style("fill", valueColor)
      .style("font-family", "Poppins, sans-serif")
      .style("font-size", isMobile ? "10px" : "12px")
      .style("font-weight", "500")
      .text(d => `${d.value}%`);


chart.selectAll(".diff-pill")
  .data(normalizedData.filter(d => d.diff !== null))
  .enter()
  .append("g")
  .attr("transform", d => {
    const yPos = y(d.label)! + barHeight / 2;
    const xPos = innerWidth + 35;
    return `translate(${xPos}, ${yPos})`;
  })
  .each(function (d) {

    const g = d3.select(this);

    let pillColor = "#E0E0E0";
    let textColor = "#666";
    let arrow = "●";

    if (d.diff > 0) {
      pillColor = "#E6F4EA";
      textColor = "#2E7D32";
      arrow = "▲";
    }
    else if (d.diff < 0) {
      pillColor = "#FDECEA";
      textColor = "#C62828";
      arrow = "▼";
    }

    const text =  `${arrow} ${Math.abs(d.diff)}`;

    const textWidth = text.length * 7 + 14;

    g.append("rect")
      .attr("x", -6)
      .attr("y", -10)
      .attr("rx", 10)
      .attr("height", 20)
      .attr("width", textWidth)
      .attr("fill", pillColor);

    g.append("text")
      .attr("text-anchor", "start")
      .attr("dy", "0.35em")
      .style("font-size", isMobile ? "9px" : "11px")
      .style("font-family", "Poppins, sans-serif")
      .style("fill", textColor)
      .text(text);
  });
  };

  render() {
    const { data } = this.props;
    if (!data || !data.length) {
      return (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
          color: "#707070",
          fontSize: 14,
          fontWeight: 500
        }}>
          <img
            src={noDataPlaceholder}
            alt="No Data"
          />
        </div>
      );
    }
    return <svg ref={this.svgRef} />;
  }
}

export default HorizontalPercentageBarChart;
